import { lastValueFrom } from 'rxjs';
import { VendorService } from '../services/vendor/vendor.service';

export interface VendorOption {
  id: string | number;
  value: string | number;
  label: string;
  vendor_code?: string;
  company_name?: string;
  [key: string]: unknown;
}

export interface VendorAddress {
  value: string | number;
  label: string;
  gst_number?: string;
  [key: string]: unknown;
}

export interface VendorTypeaheadResult {
  options: VendorOption[];
  addresses: Map<string, VendorAddress[]>;
  selectedVendor?: VendorOption;
  error?: string;
}

export interface VendorSearchConfig {
  type?: 'non-draft' | 'draft' | 'all';
  limit?: number;
  searchField?: 'company_name' | 'vendor_code' | 'email' | 'query';
  includeDeleted?: boolean;
  includeCancelled?: boolean;
  /** When true (default), only active vendors are returned (inactive ones are hidden from selection). */
  activeOnly?: boolean;
  debounceMs?: number;
  entity_type?: string;
}

interface VendorApiResponse {
  id: string | number;
  company_name?: string;
  vendor_code?: string;
  tax_information?: TaxInformation[];
  [key: string]: unknown;
}

interface TaxInformation {
  id: string | number;
  address?: string;
  gst_number?: string;
  [key: string]: unknown;
}

const DEFAULT_CONFIG: Required<VendorSearchConfig> = {
  type: 'non-draft',
  limit: 50,
  searchField: 'query',
  includeDeleted: false,
  includeCancelled: false,
  activeOnly: true,
  debounceMs: 300,
  entity_type: 'vendor',
};

export class VendorTypeaheadUtils {
  
  static normalizeVendorId(value: unknown): string | number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'object' && value !== null) {
      const obj = value as Record<string, unknown>;
      
      if (obj['value'] !== undefined && obj['value'] !== null && obj['value'] !== '') {
        return obj['value'] as string | number;
      }
      
      if (obj['id'] !== undefined && obj['id'] !== null && obj['id'] !== '') {
        return obj['id'] as string | number;
      }
      
      return null;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    return null;
  }

  static areVendorIdsEqual(
    id1: string | number | null | undefined,
    id2: string | number | null | undefined
  ): boolean {
    if (id1 === null || id1 === undefined || id2 === null || id2 === undefined) {
      return false;
    }
    return String(id1) === String(id2);
  }

  static formatVendorOption(vendor: VendorApiResponse): VendorOption {
    const { id, company_name, vendor_code, ...rest } = vendor;
    
    // Format label as "vendor_code - company_name"
    let label: string;
    if (vendor_code && company_name) {
      label = `${vendor_code} - ${company_name}`;
    } else if (vendor_code) {
      label = vendor_code;
    } else if (company_name) {
      label = company_name;
    } else {
      label = `Vendor ${id}`;
    }
    
    return {
      ...rest,
      id,
      value: id,
      label,
      vendor_code,
      company_name,
    };
  }

  static formatVendorOptions(vendors: VendorApiResponse[]): VendorOption[] {
    return vendors.map(vendor => this.formatVendorOption(vendor));
  }

  static extractVendorAddresses(vendor: VendorApiResponse): VendorAddress[] {
    const addresses: VendorAddress[] = [];
    
    if (!Array.isArray(vendor.tax_information)) {
      return addresses;
    }

    for (const info of vendor.tax_information) {
      if (info?.address) {
        addresses.push({
          value: info.id,
          label: info.address,
          gst_number: info.gst_number,
          ...info,
        });
      }
    }

    return addresses;
  }

  static async searchVendors(
    query: string,
    vendorService: VendorService,
    currentVendorId?: string | number | null,
    existingOptions: VendorOption[] = [],
    config: VendorSearchConfig | number = {}
  ): Promise<VendorTypeaheadResult> {
    const resolvedConfig: VendorSearchConfig = typeof config === 'number' 
      ? { limit: config } 
      : config;
    const mergedConfig = { ...DEFAULT_CONFIG, ...resolvedConfig };
    const normalizedQuery = (query || '').trim();

    if (!normalizedQuery) {
      return this.createEmptyResult(currentVendorId, existingOptions);
    }

    try {
      const response = await lastValueFrom(
        vendorService.getAllVendors({
          type: mergedConfig.type,
          [mergedConfig.searchField]: normalizedQuery,
          limit: mergedConfig.limit,
          is_deleted: mergedConfig.includeDeleted,
          entity_type: mergedConfig.entity_type,
          include_cancelled: mergedConfig.includeCancelled,
          ...(mergedConfig.activeOnly ? { is_active: true } : {}),
        })
      );

      const vendors: VendorApiResponse[] = response?.data?.vendors || [];
      const vendorOptions = this.formatVendorOptions(vendors);

      // const mergedOptions = this.mergeOptions(
      //   vendorOptions,
      //   existingOptions,
      //   currentVendorId
      // );

      const addressMap = this.buildAddressMap(vendorOptions);
      const selectedVendor = this.findSelectedVendor(vendorOptions, currentVendorId);

      return {
        options: vendorOptions,
        addresses: addressMap,
        selectedVendor,
      };
    } catch (error) {
      console.error('[VendorTypeaheadUtils] Search error:', error);
      return {
        options: existingOptions,
        addresses: new Map(),
        selectedVendor: this.findSelectedVendor(existingOptions, currentVendorId),
        error: error instanceof Error ? error.message : 'Search failed',
      };
    }
  }

  static async loadVendorById(
    vendorId: string | number,
    vendorService: VendorService
  ): Promise<VendorTypeaheadResult> {
    try {
      const response = await lastValueFrom(
        vendorService.getSingleVendor(String(vendorId))
      );

      const vendor: VendorApiResponse | undefined = 
        response?.data?.vendor || response?.data;

      if (!vendor) {
        return {
          options: [],
          addresses: new Map(),
          error: 'Vendor not found',
        };
      }

      const vendorOption = this.formatVendorOption(vendor);
      const addresses = this.extractVendorAddresses(vendor);

      const addressMap = new Map<string, VendorAddress[]>();
      if (addresses.length > 0) {
        addressMap.set(String(vendorId), addresses);
      }

      return {
        options: [vendorOption],
        addresses: addressMap,
        selectedVendor: vendorOption,
      };
    } catch (error) {
      console.error('[VendorTypeaheadUtils] Load error:', error);
      return {
        options: [],
        addresses: new Map(),
        error: error instanceof Error ? error.message : 'Failed to load vendor',
      };
    }
  }

  private static createEmptyResult(
    currentVendorId: string | number | null | undefined,
    existingOptions: VendorOption[]
  ): VendorTypeaheadResult {
    return {
      options: [],
      addresses: new Map(),
      selectedVendor: this.findSelectedVendor(existingOptions, currentVendorId),
    };
  }

  private static mergeOptions(
    newOptions: VendorOption[],
    existingOptions: VendorOption[],
    currentVendorId?: string | number | null
  ): VendorOption[] {
    const preservedOptions = currentVendorId
      ? existingOptions.filter(opt => this.areVendorIdsEqual(opt.value, currentVendorId))
      : [];

    const merged = [...preservedOptions, ...newOptions];
    const uniqueMap = new Map<string, VendorOption>();
    
    for (const opt of merged) {
      const key = String(opt.value);
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, opt);
      }
    }

    return Array.from(uniqueMap.values());
  }

  private static buildAddressMap(
    vendorOptions: VendorOption[]
  ): Map<string, VendorAddress[]> {
    const addressMap = new Map<string, VendorAddress[]>();
    
    for (const vendor of vendorOptions) {
      const addresses = this.extractVendorAddresses(vendor as VendorApiResponse);
      if (addresses.length > 0) {
        addressMap.set(String(vendor.value ?? vendor.id), addresses);
      }
    }

    return addressMap;
  }

  private static findSelectedVendor(
    options: VendorOption[],
    vendorId?: string | number | null
  ): VendorOption | undefined {
    if (!vendorId) {
      return undefined;
    }
    return options.find(opt => this.areVendorIdsEqual(opt.value, vendorId));
  }
}
